import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, RotateCcw, CalendarClock, XCircle } from "lucide-react";
import { fmtWhen } from "@/components/SchedulePicker";

export const notifyMailJobs = () => window.dispatchEvent(new Event("mailjobs"));
export const notifyMailJobsDone = () => window.dispatchEvent(new Event("mailjobs-done"));

function List({ title, items, tone, testid }) {
  if (!items.length) return null;
  return (
    <div className={tone} data-testid={testid}>
      <b>{title}:</b> {items.map((s) => `${s.name} (${s.reason})`).join(", ")}
    </div>
  );
}

export function MailJobProgress({ jobId, onJob }) {
  const [j, setJ] = useState(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let stop = false;
    let t;
    const load = () => api.get(`/mail-jobs/${jobId}`).then((r) => {
      if (stop) return;
      setJ(r.data);
      if (r.data.status === "running") t = setTimeout(load, 2000);
      else if (r.data.status !== "scheduled") { notifyMailJobs(); notifyMailJobsDone(); }
    }).catch(() => {});
    load();
    return () => { stop = true; clearTimeout(t); };
  }, [jobId, tick]);
  if (!j) return <div className="py-6 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>;
  if (j.status === "scheduled" || j.status === "cancelled") {
    const cancel = async () => {
      if (!window.confirm("Geplanten Versand wirklich stornieren?")) return;
      try { await api.post(`/mail-jobs/${j.id}/cancel`); toast.success("Versand storniert"); notifyMailJobs(); setTick((x) => x + 1); }
      catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    };
    return (
      <div className="space-y-3 text-sm" data-testid="mail-job-scheduled">
        <div className="flex items-center gap-2 font-medium text-slate-800"><CalendarClock size={16} className="text-blue-600" />
          {j.status === "cancelled" ? "Geplanter Versand wurde storniert." : `Geplant für ${fmtWhen(j.scheduled_at)} – ${j.total} Empfänger`}</div>
        {j.status === "scheduled" && <p className="text-xs text-slate-500">Sie können dieses Fenster schließen. Oben rechts sehen Sie den geplanten Versand, am Ende erhalten Sie eine Zusammenfassung per Mail.</p>}
        {j.status === "scheduled" && <Button variant="outline" size="sm" onClick={cancel} className="text-red-700" data-testid="mail-job-cancel"><XCircle size={14} className="mr-1.5" /> Versand stornieren</Button>}
      </div>
    );
  }
  const done = j.pos ?? (j.sent.length + j.skipped.length + j.failed.length);
  const pct = j.total ? Math.round((done / j.total) * 100) : 100;
  const retry = async () => {
    try { const { data } = await api.post(`/mail-jobs/${j.id}/retry`); onJob?.(data.job_id); notifyMailJobs(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3 text-sm" data-testid="mail-job-progress">
      <div className="flex items-center justify-between">
        <span className="font-medium text-slate-800" data-testid="mail-job-count">{j.status === "running" ? `${done} von ${j.total} verarbeitet …` : `Abgeschlossen: ${done} von ${j.total}`}</span>
        <span className="text-slate-500">{pct} %</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-600 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      <div className="text-emerald-700 font-medium" data-testid="mail-job-sent">{j.sent.length} Mail(s) gesendet</div>
      <List title="Übersprungen" items={j.skipped} tone="text-amber-800" testid="mail-job-skipped" />
      <List title="Fehlgeschlagen" items={j.failed} tone="text-red-700" testid="mail-job-failed" />
      {j.status === "running" && <p className="text-xs text-slate-500">Der Versand läuft im Hintergrund (ca. 20 Mails pro Minute). Sie können dieses Fenster schließen – oben rechts sehen Sie den Fortschritt, am Ende erhalten Sie eine Zusammenfassung per Mail.</p>}
      {j.status !== "running" && j.failed.length > 0 && (
        <Button variant="outline" size="sm" onClick={retry} data-testid="mail-job-retry"><RotateCcw size={14} className="mr-1.5" /> Fehlgeschlagene erneut senden ({j.failed.length})</Button>
      )}
    </div>
  );
}
